/**
 * CreativeWorkspaceSplit — top-level split-pane layout for the workspace.
 *
 * - Left 40% (sticky): CreativeViewer
 * - Right 60% (scrollable): CreativeAnalysisPanel
 * - Below 1024px: stacks (viewer above, panel below)
 */

import type { ReactNode } from 'react';
import { CreativeAnalysisPanel } from './CreativeAnalysisPanel';
import { CreativeViewer } from './CreativeViewer';
import type {
  AnalysisTab,
  CreativeSiblingSummary,
  CreativeWorkspaceData,
  PDOFitConcept,
  WorkspaceBriefAngle,
} from './types';

interface Props {
  data: CreativeWorkspaceData;
  siblings: CreativeSiblingSummary[];
  siblingStrip?: ReactNode;
  activePersona: string | null;
  onPersonaChange: (id: string) => void;
  activeTab: AnalysisTab;
  onTabChange: (tab: AnalysisTab) => void;
  linkedAngle?: WorkspaceBriefAngle | null;
  onApplyToBrief?: (mod: Record<string, unknown>) => void;
  onTestVariant?: (mod: Record<string, unknown>, index: number) => void;
  onGapClick?: (concept: PDOFitConcept) => void;
}

export function CreativeWorkspaceSplit({
  data,
  siblings,
  siblingStrip,
  activePersona,
  onPersonaChange,
  activeTab,
  onTabChange,
  linkedAngle,
  onApplyToBrief,
  onTestVariant,
  onGapClick,
}: Props) {
  return (
    <div className="flex flex-col lg:flex-row gap-0 min-h-0">
      <div className="lg:w-[40%] lg:flex-shrink-0">
        <CreativeViewer
          creative={data.creative}
          bestMediaUrl={data.best_media_url}
          siblingStrip={siblingStrip}
        />
      </div>

      <div className="lg:w-[60%] lg:overflow-y-auto lg:h-[calc(100vh-72px)]">
        <CreativeAnalysisPanel
          data={data}
          siblings={siblings}
          activePersona={activePersona}
          onPersonaChange={onPersonaChange}
          activeTab={activeTab}
          onTabChange={onTabChange}
          linkedAngle={linkedAngle ?? null}
          onApplyToBrief={onApplyToBrief}
          onTestVariant={onTestVariant}
          onGapClick={onGapClick}
        />
      </div>
    </div>
  );
}
